import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUpDown, Copy, Download, Shuffle, Filter, Eye, Settings, ArrowUp, ArrowDown } from 'lucide-react';

interface SortMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface SortOptions {
  direction: 'asc' | 'desc';
  caseSensitive: boolean;
  trimWhitespace: boolean;
  ignoreEmptyLines: boolean;
  removeDuplicates: boolean;
  ignoreLeadingNumbers: boolean;
  ignoreLeadingSpecialChars: boolean;
  naturalNumberSort: boolean;
  preserveOriginalIndices: boolean;
  randomSeed: number;
  customSeparator: string;
  reverseAfterSort: boolean;
  shuffleBeforeSort: boolean;
}

interface SortResult {
  originalLines: string[];
  sortedLines: string[];
  statistics: {
    originalCount: number;
    finalCount: number;
    duplicatesRemoved: number;
    emptyLinesRemoved: number;
    longestLine: string;
    shortestLine: string;
    averageLength: number;
  };
}

const SortLines: React.FC = () => {
  const [input, setInput] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('alphabetical');
  const [result, setResult] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [options, setOptions] = useState<SortOptions>({
    direction: 'asc',
    caseSensitive: false,
    trimWhitespace: true,
    ignoreEmptyLines: true,
    removeDuplicates: false,
    ignoreLeadingNumbers: false,
    ignoreLeadingSpecialChars: false,
    naturalNumberSort: true,
    preserveOriginalIndices: false,
    randomSeed: Math.floor(Math.random() * 1000),
    customSeparator: '',
    reverseAfterSort: false,
    shuffleBeforeSort: false
  });

  const sortMethods: SortMethod[] = [
    {
      id: 'alphabetical',
      name: 'Alphabetical',
      description: 'Standard A-Z alphabetical sorting',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 'length',
      name: 'Line Length',
      description: 'Sort by number of characters',
      icon: <Filter className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 'numeric',
      name: 'Numeric',
      description: 'Sort lines by numeric value',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'purple'
    },
    {
      id: 'natural',
      name: 'Natural Sort',
      description: 'Human-friendly alphanumeric sorting',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 'reverse',
      name: 'Reverse Order',
      description: 'Reverse the current line order',
      icon: <ArrowDown className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      id: 'random',
      name: 'Random Shuffle',
      description: 'Randomly shuffle all lines',
      icon: <Shuffle className="w-4 h-4" />,
      color: 'indigo'
    },
    {
      id: 'wordCount',
      name: 'Word Count',
      description: 'Sort by number of words per line',
      icon: <Filter className="w-4 h-4" />,
      color: 'pink'
    },
    {
      id: 'lastWord',
      name: 'Last Word',
      description: 'Sort by the last word in each line',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'orange'
    },
    {
      id: 'firstWord',
      name: 'First Word',
      description: 'Sort by the first word in each line',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'teal'
    },
    {
      id: 'vowelCount',
      name: 'Vowel Count',
      description: 'Sort by number of vowels',
      icon: <Filter className="w-4 h-4" />,
      color: 'cyan'
    },
    {
      id: 'consonantCount',
      name: 'Consonant Count',
      description: 'Sort by number of consonants',
      icon: <Filter className="w-4 h-4" />,
      color: 'emerald'
    },
    {
      id: 'date',
      name: 'Date Sort',
      description: 'Sort lines containing dates',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'violet'
    },
    {
      id: 'domain',
      name: 'Domain Sort',
      description: 'Sort email addresses by domain',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'rose'
    },
    {
      id: 'frequency',
      name: 'Character Frequency',
      description: 'Sort by unique character count',
      icon: <Filter className="w-4 h-4" />,
      color: 'amber'
    },
    {
      id: 'complexity',
      name: 'Text Complexity',
      description: 'Sort by text complexity score',
      icon: <Filter className="w-4 h-4" />,
      color: 'lime'
    },
    {
      id: 'custom',
      name: 'Custom Pattern',
      description: 'Sort by custom separator position',
      icon: <Settings className="w-4 h-4" />,
      color: 'slate'
    }
  ];

  const processLines = (lines: string[], method: string, opts: SortOptions): SortResult => {
    let processedLines = [...lines];
    let originalLines = [...lines];

    // Apply preprocessing
    if (opts.trimWhitespace) {
      processedLines = processedLines.map(line => line.trim());
    }

    if (opts.ignoreEmptyLines) {
      processedLines = processedLines.filter(line => line !== '');
    }

    if (opts.removeDuplicates) {
      processedLines = Array.from(new Set(processedLines));
    }

    if (opts.shuffleBeforeSort && method !== 'random') {
      processedLines = shuffleArray(processedLines, opts.randomSeed);
    }

    // Create sortable items with original indices
    const sortableItems = processedLines.map((line, index) => ({
      line,
      originalIndex: index,
      sortKey: getSortKey(line, method, opts)
    }));

    // Apply sorting
    let sortedItems = [...sortableItems];
    
    switch (method) {
      case 'random':
        sortedItems = shuffleArray(sortedItems, opts.randomSeed);
        break;
      case 'reverse':
        sortedItems.reverse();
        break;
      default:
        sortedItems.sort((a, b) => {
          const aKey = a.sortKey;
          const bKey = b.sortKey;
          
          if (typeof aKey === 'number' && typeof bKey === 'number') {
            return opts.direction === 'asc' ? aKey - bKey : bKey - aKey;
          }
          
          const aStr = String(aKey);
          const bStr = String(bKey);
          
          const comparison = opts.caseSensitive 
            ? aStr.localeCompare(bStr, undefined, { numeric: opts.naturalNumberSort })
            : aStr.localeCompare(bStr, undefined, { numeric: opts.naturalNumberSort, sensitivity: 'base' });
            
          return opts.direction === 'asc' ? comparison : -comparison;
        });
    }

    if (opts.reverseAfterSort) {
      sortedItems.reverse();
    }

    let finalLines = sortedItems.map(item => 
      opts.preserveOriginalIndices ? `${item.originalIndex + 1}. ${item.line}` : item.line
    );

    // Calculate statistics
    const nonEmptyLines = processedLines.filter(line => line !== '');
    const statistics = {
      originalCount: originalLines.length,
      finalCount: finalLines.length,
      duplicatesRemoved: processedLines.length - Array.from(new Set(processedLines)).length,
      emptyLinesRemoved: originalLines.length - processedLines.length,
      longestLine: nonEmptyLines.reduce((a, b) => a.length > b.length ? a : b, ''),
      shortestLine: nonEmptyLines.reduce((a, b) => a.length < b.length ? a : b, nonEmptyLines[0] || ''),
      averageLength: nonEmptyLines.length > 0 ? Math.round(nonEmptyLines.reduce((sum, line) => sum + line.length, 0) / nonEmptyLines.length) : 0
    };

    return {
      originalLines,
      sortedLines: finalLines,
      statistics
    };
  };

  const getSortKey = (line: string, method: string, opts: SortOptions): string | number => {
    let processedLine = line;
    
    if (opts.ignoreLeadingNumbers) {
      processedLine = processedLine.replace(/^\d+\.?\s*/, '');
    }
    
    if (opts.ignoreLeadingSpecialChars) {
      processedLine = processedLine.replace(/^[^\w\s]+/, '');
    }

    switch (method) {
      case 'alphabetical':
        return processedLine;
      case 'length':
        return processedLine.length;
      case 'numeric':
        const num = parseFloat(processedLine.replace(/[^\d.-]/g, ''));
        return isNaN(num) ? 0 : num;
      case 'natural':
        return processedLine;
      case 'wordCount':
        return processedLine.trim().split(/\s+/).filter(w => w).length;
      case 'lastWord':
        const words = processedLine.trim().split(/\s+/);
        return words[words.length - 1] || '';
      case 'firstWord':
        return processedLine.trim().split(/\s+/)[0] || '';
      case 'vowelCount':
        return (processedLine.match(/[aeiouAEIOU]/g) || []).length;
      case 'consonantCount':
        return (processedLine.match(/[bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXYZ]/g) || []).length;
      case 'date':
        const dateMatch = processedLine.match(/\d{1,4}[-\/]\d{1,2}[-\/]\d{1,4}/);
        if (dateMatch) {
          const date = new Date(dateMatch[0]);
          return isNaN(date.getTime()) ? 0 : date.getTime();
        }
        return 0;
      case 'domain':
        const emailMatch = processedLine.match(/@([^@\s]+)/);
        return emailMatch ? emailMatch[1] : processedLine;
      case 'frequency':
        return new Set(processedLine.toLowerCase()).size;
      case 'complexity':
        // Simple complexity score based on character variety
        const hasNumbers = /\d/.test(processedLine);
        const hasUpper = /[A-Z]/.test(processedLine);
        const hasLower = /[a-z]/.test(processedLine);
        const hasSpecial = /[^a-zA-Z0-9\s]/.test(processedLine);
        const uniqueChars = new Set(processedLine).size;
        return (hasNumbers ? 1 : 0) + (hasUpper ? 1 : 0) + (hasLower ? 1 : 0) + (hasSpecial ? 1 : 0) + uniqueChars;
      case 'custom':
        if (opts.customSeparator) {
          const parts = processedLine.split(opts.customSeparator);
          return parts[1] || parts[0] || '';
        }
        return processedLine;
      default:
        return processedLine;
    }
  };

  const shuffleArray = <T,>(array: T[], seed: number): T[] => {
    const shuffled = [...array];
    let random = seed;
    for (let i = shuffled.length - 1; i > 0; i--) {
      random = (random * 9301 + 49297) % 233280;
      const j = Math.floor((random / 233280) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const sortResult: SortResult = useMemo(() => {
    if (!input.trim()) {
      return {
        originalLines: [],
        sortedLines: [],
        statistics: {
          originalCount: 0,
          finalCount: 0,
          duplicatesRemoved: 0,
          emptyLinesRemoved: 0,
          longestLine: '',
          shortestLine: '',
          averageLength: 0
        }
      };
    }

    const lines = input.split('\n');
    return processLines(lines, selectedMethod, options);
  }, [input, selectedMethod, options]);

  useEffect(() => {
    setResult(sortResult.sortedLines.join('\n'));
  }, [sortResult]);

  const copyToClipboard = async () => {
    if (result) {
      await navigator.clipboard.writeText(result);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadResult = () => {
    if (result) {
      const stats = `Sorting Summary:
Method: ${sortMethods.find(m => m.id === selectedMethod)?.name}
Direction: ${options.direction.toUpperCase()}
Original lines: ${sortResult.statistics.originalCount}
Final lines: ${sortResult.statistics.finalCount}
${sortResult.statistics.duplicatesRemoved > 0 ? `Duplicates removed: ${sortResult.statistics.duplicatesRemoved}\n` : ''}
${sortResult.statistics.emptyLinesRemoved > 0 ? `Empty lines removed: ${sortResult.statistics.emptyLinesRemoved}\n` : ''}

Sorted Text:
${result}`;

      const blob = new Blob([stats], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sorted-lines.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleText = () => {
    const samples = [
      `zebra
apple
Banana
cherry
date
Elderberry
fig
grape
honeydew
10 kiwi
2 lemon
100 mango`,
      `john.doe@gmail.com
alice@yahoo.com
bob.smith@outlook.com
charlie@gmail.com
diana@company.org
eve@test.co.uk
frank@domain.net`,
      `Task 1: Complete project documentation
Task 10: Review code changes
Task 2: Fix critical bug
Task 20: Deploy to production
Task 3: Update user interface
Task 100: Archive old files`,
      `2024-01-15 Meeting with client
2023-12-20 Project kickoff
2024-02-01 Milestone review
2023-11-30 Requirements gathering
2024-01-05 Design approval
2024-03-10 Final delivery`,
      `The quick brown fox
A simple test
Programming is fun
Machine learning algorithms
AI and data science
Web development tools
Software engineering practices`,
      `Item A (5 units)
Product B (15 units)  
Service C (2 units)
Tool D (50 units)
Resource E (8 units)
Component F (100 units)`
    ];
    setInput(samples[Math.floor(Math.random() * samples.length)]);
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800',
      rose: 'bg-rose-50 border-rose-200 text-rose-800',
      amber: 'bg-amber-50 border-amber-200 text-amber-800',
      lime: 'bg-lime-50 border-lime-200 text-lime-800',
      slate: 'bg-slate-50 border-slate-200 text-slate-800'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <ArrowUpDown className="text-blue-600" />
            Advanced Line Sorter
          </h1>
          <p className="text-gray-600">Powerful text sorting with multiple algorithms and customization options</p>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <label className="text-lg font-semibold text-gray-700">Input Text</label>
            <div className="flex gap-2">
              <button
                onClick={generateSampleText}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Text
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Settings size={16} />
                {showOptions ? 'Hide' : 'Show'} Options
              </button>
              <button
                onClick={() => setShowStatistics(!showStatistics)}
                className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter lines to sort here..."
                className="w-full h-40 p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none resize-none text-gray-700 transition-colors"
              />
              <div className="text-sm text-gray-500 mt-2">
                Lines: {input ? input.split('\n').length : 0}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">Sorted Result</label>
                <div className="flex gap-2">
                  <button
                    onClick={copyToClipboard}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadResult}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={result}
                readOnly
                className="w-full h-40 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none"
                placeholder="Sorted lines will appear here..."
              />
              <div className="text-sm text-gray-500 mt-2">
                Lines: {result ? result.split('\n').length : 0}
              </div>
            </div>
          </div>
        </div>

        {/* Sort Options */}
        {showOptions && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Sorting Options</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Basic Settings</h3>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Sort Direction</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setOptions({...options, direction: 'asc'})}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                        options.direction === 'asc' 
                          ? 'bg-blue-100 text-blue-800 border-2 border-blue-300' 
                          : 'bg-gray-100 text-gray-700 border-2 border-gray-200'
                      }`}
                    >
                      <ArrowUp size={16} />
                      Ascending
                    </button>
                    <button
                      onClick={() => setOptions({...options, direction: 'desc'})}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                        options.direction === 'desc' 
                          ? 'bg-blue-100 text-blue-800 border-2 border-blue-300' 
                          : 'bg-gray-100 text-gray-700 border-2 border-gray-200'
                      }`}
                    >
                      <ArrowDown size={16} />
                      Descending
                    </button>
                  </div>
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.caseSensitive}
                    onChange={(e) => setOptions({...options, caseSensitive: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Case Sensitive</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.naturalNumberSort}
                    onChange={(e) => setOptions({...options, naturalNumberSort: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Natural Number Sort</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Processing</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.trimWhitespace}
                    onChange={(e) => setOptions({...options, trimWhitespace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Trim Whitespace</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.ignoreEmptyLines}
                    onChange={(e) => setOptions({...options, ignoreEmptyLines: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Ignore Empty Lines</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeDuplicates}
                    onChange={(e) => setOptions({...options, removeDuplicates: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Duplicates</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.ignoreLeadingNumbers}
                    onChange={(e) => setOptions({...options, ignoreLeadingNumbers: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Ignore Leading Numbers</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Advanced</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.preserveOriginalIndices}
                    onChange={(e) => setOptions({...options, preserveOriginalIndices: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Show Original Line Numbers</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.reverseAfterSort}
                    onChange={(e) => setOptions({...options, reverseAfterSort: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Reverse After Sort</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.shuffleBeforeSort}
                    onChange={(e) => setOptions({...options, shuffleBeforeSort: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Shuffle Before Sort</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Random Seed</label>
                  <input
                    type="number"
                    value={options.randomSeed}
                    onChange={(e) => setOptions({...options, randomSeed: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>

                {selectedMethod === 'custom' && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Custom Separator</label>
                    <input
                      type="text"
                      value={options.customSeparator}
                      onChange={(e) => setOptions({...options, customSeparator: e.target.value})}
                      placeholder="e.g., : or |"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sort Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <ArrowUpDown className="text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Sorting Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Statistics */}
        {showStatistics && input && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-green-600" />
              <h2 className="text-xl font-semibold text-gray-800">Sorting Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Original</h3>
                <div className="text-2xl font-bold text-blue-900">{sortResult.statistics.originalCount}</div>
                <p className="text-sm text-blue-600">Total lines</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Final</h3>
                <div className="text-2xl font-bold text-green-900">{sortResult.statistics.finalCount}</div>
                <p className="text-sm text-green-600">Lines processed</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Average Length</h3>
                <div className="text-2xl font-bold text-orange-900">{sortResult.statistics.averageLength}</div>
                <p className="text-sm text-orange-600">Characters per line</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Method</h3>
                <div className="text-lg font-bold text-purple-900">{sortMethods.find(m => m.id === selectedMethod)?.name}</div>
                <p className="text-sm text-purple-600">{options.direction.toUpperCase()}</p>
              </div>
            </div>

            {(sortResult.statistics.duplicatesRemoved > 0 || sortResult.statistics.emptyLinesRemoved > 0) && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {sortResult.statistics.duplicatesRemoved > 0 && (
                  <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                    <h3 className="font-semibold text-red-800 mb-2">Duplicates Removed</h3>
                    <div className="text-2xl font-bold text-red-900">{sortResult.statistics.duplicatesRemoved}</div>
                    <p className="text-sm text-red-600">Duplicate lines found</p>
                  </div>
                )}

                {sortResult.statistics.emptyLinesRemoved > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                    <h3 className="font-semibold text-yellow-800 mb-2">Empty Lines Removed</h3>
                    <div className="text-2xl font-bold text-yellow-900">{sortResult.statistics.emptyLinesRemoved}</div>
                    <p className="text-sm text-yellow-600">Blank lines filtered</p>
                  </div>
                )}
              </div>
            )}

            {sortResult.statistics.longestLine && sortResult.statistics.shortestLine && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Line Analysis:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-1">Longest Line ({sortResult.statistics.longestLine.length} chars)</h4>
                    <div className="text-sm text-emerald-700 font-mono truncate">{sortResult.statistics.longestLine}</div>
                  </div>
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <h4 className="font-semibold text-teal-800 mb-1">Shortest Line ({sortResult.statistics.shortestLine.length} chars)</h4>
                    <div className="text-sm text-teal-700 font-mono truncate">{sortResult.statistics.shortestLine}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Sample Preview */}
            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-3">Preview (First 5 Lines):</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-2">Original Order:</h4>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    {sortResult.originalLines.slice(0, 5).map((line, index) => (
                      <div key={index} className="text-sm text-gray-700 font-mono truncate py-1">
                        {index + 1}. {line}
                      </div>
                    ))}
                    {sortResult.originalLines.length > 5 && (
                      <div className="text-sm text-gray-500 italic">...and {sortResult.originalLines.length - 5} more lines</div>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-2">Sorted Order:</h4>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    {sortResult.sortedLines.slice(0, 5).map((line, index) => (
                      <div key={index} className="text-sm text-blue-700 font-mono truncate py-1">
                        {index + 1}. {line}
                      </div>
                    ))}
                    {sortResult.sortedLines.length > 5 && (
                      <div className="text-sm text-blue-500 italic">...and {sortResult.sortedLines.length - 5} more lines</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SortLines;